import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import "./StartingQuestionnaire.css";
import { DEBUG_MODE } from "../../config/appConfig";
import { getDebugEndingQuestionnaireData } from "../../utils/debugPrefill";

/**
 * Ending questionnaire – SUS + open-ended questions.
 * When Prolific params are provided, shows a redirect button after submission.
 *
 * @param {Object} props
 * @param {Function} props.handleFormSubmission
 * @param {string|null} [props.prolificRedirectUrl] - Prolific completion redirect URL.
 */
function EndingQuestionnaire({ handleFormSubmission, prolificRedirectUrl, debugPrefill = false }) {
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    sus: {
      frequentUse: "",
      complex: "",
      easyUse: "",
      needSupport: "",
      wellIntegrated: "",
      inconsistent: "",
      quickLearn: "",
      cumbersome: "",
      confident: "",
      needLearning: "",
    },
    trustThinking: "",
    photobookUsefulness: "",
    systemImprovements: "",
    additionalComments: "",
  });

  useEffect(() => {
    if (debugPrefill) {
      setFormData(getDebugEndingQuestionnaireData());
      setPage(2);
    }
  }, [debugPrefill]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSusChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      sus: {
        ...prev.sus,
        [field]: value,
      },
    }));
  };

  const nextPage = () => {
    const form = document.querySelector('form');
    if (form && form.reportValidity()) {
      setPage((prev) => prev + 1);
    }
  };
  const prevPage = () => setPage((prev) => prev - 1);


  const renderPage = () => {
    switch (page) {
      case 1:
        return (
          <div className="page">
            <h2>Please Tell Us How Usable The Photobook AI Felt *</h2>

            <div className="section">
              <p className="scale-info">
                Please indicate your level of agreement with the following statements:
              </p>

              <table className="likert-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>
                      1
                      <div style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginTop: 2 }}>
                        Strongly disagree
                      </div>
                    </th>
                    <th>2</th>
                    <th>3</th>
                    <th>4</th>
                    <th>
                      5
                      <div style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginTop: 2 }}>
                        Strongly agree
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { field: "frequentUse", text: "I think that I would like to use this system frequently." },
                    { field: "complex", text: "I found the system unnecessarily complex." },
                    { field: "easyUse", text: "I thought the system was easy to use." },
                    { field: "needSupport", text: "I think that I would need the support of a technical person to be able to use this system." },
                    { field: "wellIntegrated", text: "I found the various functions in this system were well integrated." },
                    { field: "inconsistent", text: "I thought there was too much inconsistency in this system." },
                    { field: "quickLearn", text: "I would imagine that most people would learn to use this system very quickly." },
                    { field: "cumbersome", text: "I found the system very cumbersome to use." },
                    { field: "confident", text: "I felt very confident using the system." },
                    { field: "needLearning", text: "I needed to learn a lot of things before I could get going with this system." },
                  ].map((question, index) => (
                    <tr key={question.field}>
                      <td>
                        {index + 1}. {question.text}
                      </td>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={`sus-${question.field}`}
                            checked={formData.sus[question.field] === value.toString()}
                            onChange={() => handleSusChange(question.field, value.toString())}
                            required
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="page">
            <h2>Final Questions</h2>

            <div className="section">
              <div className="form-group">
                <label>
                  1. During the study, you answered several questions about trust. When you were giving your ratings, what parts of the system were you thinking about most? *
                </label>
                <textarea
                  name="trustThinking"
                  value={formData.trustThinking}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  2. Imagine you were creating a personal photobook for your holiday. Would you find a system like this useful? Which features did you like, and which ones not so much? *
                </label>
                <textarea
                  name="photobookUsefulness"
                  value={formData.photobookUsefulness}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  3. How could this system be more useful to you? Is there anything that you wish you could have seen or done? *
                </label>
                <textarea
                  name="systemImprovements"
                  value={formData.systemImprovements}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  4. Is there anything else you would like to share about your experience today?
                </label>
                <textarea
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="page">
            <h1>Thank You!</h1>
            <p>Your responses have been submitted successfully.</p>
            {prolificRedirectUrl && (
              <Button
                variant="contained"
                color="success"
                href={prolificRedirectUrl}
                sx={{ mt: 2 }}
              >
                Return to Prolific
              </Button>
            )}
          </div>
        );
    }
  };

  const handleSubmission = (e) => {
    e.preventDefault();
    handleFormSubmission(formData);
    setPage((prev) => prev + 1);
  };

  const fillDebugData = () => {
    setFormData(getDebugEndingQuestionnaireData());
  };

  return (
    <div className="root-container">
      <div className="app">
        <div className="form-container">
          <h1>Before You Finish</h1>
          <p className="scale-info">
            Just a couple of short questionnaires left: how usable the Photobook
            AI felt, and a few open questions about your experience.
          </p>

          {DEBUG_MODE && (
            <Button
              variant="outlined"
              color="warning"
              onClick={fillDebugData}
              sx={{ marginBottom: 2 }}
            >
              🔧 Debug: Fill All Fields
            </Button>
          )}

          <form onSubmit={handleSubmission}>
            {renderPage()}

            {page < 3 && (
              <div className="navigation-buttons">
                {page > 1 && (
                  <Button
                    variant="contained"
                    className="back-button"
                    onClick={prevPage}
                    type="button"
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="contained"
                  className="next-button"
                  onClick={page === 2 ? undefined : nextPage}
                  type={page === 2 ? "submit" : "button"}
                >
                  {page === 2 ? "Submit" : "Next"}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default EndingQuestionnaire;
